<template>
  <div class="import-file" :class="{ open: expanded }">
    <div class="import-file__head">
      <button
        type="button"
        class="import-file__toggle"
        :disabled="!enabled || loading"
        :aria-expanded="expanded"
        @click="$emit('toggle')"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm import-file__spinner" role="status" aria-hidden="true"></span>
        <span v-else class="import-icon" :class="`tone-${icon.tone}`" aria-hidden="true">
          <i class="material-icons">{{ icon.name }}</i>
        </span>
        <span class="import-file__main">
          <span v-if="typeBadge || destinationTag" class="import-file__tags">
            <span v-if="typeBadge" class="import-file__badge" data-test="type-badge">{{ typeBadge }}</span>
            <span v-if="destinationTag" class="import-file__dest" data-test="dest-tag">{{ destinationTag }}</span>
          </span>
          <span class="import-file__name" data-test="name">{{ name }}</span>
          <span v-if="meta" class="import-file__meta" :class="`tone-${tone}`" data-test="meta">{{ meta }}</span>
        </span>
        <i class="material-icons import-file__chev" :class="{ hidden: loading }" aria-hidden="true">chevron_right</i>
      </button>
      <button
        type="button"
        class="import-file__remove"
        :disabled="!enabled"
        :aria-label="$t('import_files.remove')"
        :title="$t('import_files.remove')"
        data-test="remove"
        @click="$emit('remove')"
      >
        <i class="material-icons" aria-hidden="true">close</i>
      </button>
    </div>
    <div v-if="expanded && !loading" class="import-file__body">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ImportIcon, ImportMetaTone } from '../../utils/import/importTexts';

// One file on the Import files dialog: an icon for what it becomes, a yellow badge above its
// name with what it becomes plus a blue "→ Planned hikes" tag when that isn't the start
// screen, its name, one stats line, a chevron and ✕. Stateless, so it's tested in isolation.
withDefaults(
  defineProps<{
    name: string;
    meta?: string;
    icon: ImportIcon;
    expanded?: boolean;
    tone?: ImportMetaTone;
    typeBadge?: string | null;
    destinationTag?: string | null;
    loading?: boolean;
    enabled?: boolean;
  }>(),
  { meta: '', expanded: false, tone: 'normal', typeBadge: null, destinationTag: null, loading: false, enabled: true }
);

defineEmits<{ (e: 'toggle'): void; (e: 'remove'): void }>();
</script>

<style scoped>
.import-file {
  border: 1px solid var(--gray-border);
  border-radius: 12px;
  background: var(--gray);
  overflow: hidden;
}
.import-file.open {
  border-color: var(--yellow);
}
.import-file__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 10px;
}
.import-file__toggle {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 4px 12px 12px;
  background: none;
  border: 0;
  color: var(--white);
  text-align: left;
  cursor: pointer;
}
.import-file__toggle:disabled {
  cursor: default;
}
.import-file__spinner {
  width: 18px;
  height: 18px;
  margin: 0 7px;
  color: var(--yellow);
}
.import-file__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.import-file__tags {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}
.import-file__badge {
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  padding: 0 6px;
  border-radius: 4px;
  background: var(--yellow);
  color: var(--dark);
  white-space: nowrap;
}
.import-file__dest {
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  padding: 0 6px;
  border-radius: 4px;
  background: rgba(45, 194, 227, 0.14);
  color: var(--blue);
  white-space: nowrap;
}
.import-file__name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.import-file__meta {
  font-size: 12px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.import-file__meta.tone-warn {
  color: var(--orange);
  font-weight: 600;
}
.import-file__meta.tone-error {
  color: var(--red);
  font-weight: 600;
}
.import-file__chev {
  font-size: 20px;
  color: var(--text-muted);
  transition: transform 0.15s;
}
.import-file.open .import-file__chev {
  transform: rotate(90deg);
}
.import-file__chev.hidden {
  visibility: hidden;
}
.import-file__remove {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 0;
  background: var(--dark);
  color: var(--white);
  display: grid;
  place-items: center;
  cursor: pointer;
}
.import-file__remove .material-icons {
  font-size: 16px;
}
.import-file__remove:disabled {
  opacity: 0.5;
  cursor: default;
}
.import-file__body {
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>

<style>
/* Shared by the row, the options and the summary: an icon on a dark circle. */
.import-icon {
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #3a3c44;
  display: grid;
  place-items: center;
}
.import-icon .material-icons {
  font-size: 18px;
}
.import-icon.tone-white {
  color: var(--white);
}
.import-icon.tone-grey {
  color: var(--text-muted);
}
.import-icon.tone-red {
  color: var(--red);
}
</style>
